import type React from "react";
import { useContext, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import _ from "lodash";
import { useDispatch } from "react-redux";

import { durationFilterDSSelected, webSourceFilterDSSelected } from "app/actions/usersFiltersSelectionActions";
import { swSettings } from "app/common/services/swSettings";
import { PeriodOverPeriodWrapper } from "app/components/duration-selector/period-over-period/PeriodOverPeriodWrapper";
import { CountryFilter } from "app/components/filters-bar/country-filter/CountryFilter";
import * as utils from "app/components/filters-bar/utils";
import { FiltersEnum } from "app/components/filters-bar/utils";
import { WebSourceFilter } from "app/components/filters-bar/websource-filter/WebSourceFilter";
import type { IFilter } from "app/components/React/FiltersBar/FiltersBar";
import { FiltersBar } from "app/components/React/FiltersBar/FiltersBar";
import UnlockModal from "app/components/React/UnlockModalProvider/UnlockModalProvider";
import { i18nFilter } from "app/filters/i18nFilter";
import { handleCTAClick, TestCases } from "app/common/utils/handleGainAccessRedirect";
import { StateResolvedContext } from "app/pages/segments/StateResolvedContext";
import { updateFilterConsistencyCountry } from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import type { IUnlockModalConfigTypes } from "pro-features/components/Modals/src/UnlockModal/unlockModalConfig";
import unlockModalConfig from "pro-features/components/Modals/src/UnlockModal/unlockModalConfig";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import { SwTrack } from "app/services/SwTrack";
import { allTrackers } from "app/services/track/track";

import { preparePresets } from "../../components/dashboard/widget-wizard/components/DashboardWizardDuration";
import CountryService from "../../services/CountryService";
import { DurationSelectorSimple } from "app/components/duration-selector/DurationSelectorSimple";

import { EHooksVersions } from "app/services/SwHook";
import { DurationSelectorPresetOriented } from "pro-features/components/duration-selectors/DurationSelectedPresetOriented";

interface ISegmentsFiltersProps {
    updateFromReactComponent?: (filters) => void;
    filters?: any;
    unsupportedFilter?: boolean;
}

export const SegmentsResponsiveFilters: React.FC<ISegmentsFiltersProps> = (props) => {
    const { unsupportedFilter = false } = props;
    const { state, params } = useCurrentStateAndParams();
    const { skipFilterConsistency } = state;
    const dispatch = useDispatch();

    const durationSelected = (duration) => {
        dispatch(durationFilterDSSelected(duration));
    };
    const webSourceSelected = (webSource) => {
        dispatch(webSourceFilterDSSelected(webSource));
    };

    const stateResolvedValue = useContext(StateResolvedContext);

    const getWebSources = (duration, country, state: IRouterState) => {
        const availableWebSources = utils.getAvailableWebSource(state, { duration, country });
        if (availableWebSources.length > 0) {
            return availableWebSources;
        }
        return utils.getAvailableWebSource(
            { name: "companyresearch_segment_analysis_trafficandengagement" },
            { duration, country },
        );
    };

    const [isWebSourceHookOpen, setIsWebSourceHookOpen] = useState(false);
    const getStateParams = (params, state) => {
        const availableCountries = utils.getCountries();
        const selectedCountry = CountryService.getCountryById(params.country);
        const { duration, comparedDuration } = params;
        const availableWebsource = getWebSources(duration, params.country, state);
        const selectedWebSource = _.find(availableWebsource, { id: params.webSource ?? "Desktop" });

        // initialize state params
        let selectedDuration = duration;
        let selectedComparedDuration = comparedDuration;
        let isDurationDisabled = false;
        let minDate = swSettings.current.startDate;
        let maxDate = swSettings.current.endDate;

        // if page requires duration filter change
        if (state?.overrideDurationFilterParams) {
            selectedDuration = state.overrideDurationFilterParams.validDuration;
            selectedComparedDuration = state.overrideDurationFilterParams.validComparedDuration;
            isDurationDisabled = state.overrideDurationFilterParams.isDurationDisabled;
            minDate = state.overrideDurationFilterParams.minDate;
            maxDate = state.overrideDurationFilterParams.maxDate;
        }

        return {
            isDurationDisabled,
            selectedDuration,
            selectedComparedDuration,
            availableCountries,
            selectedCountry,
            availableWebsource,
            selectedWebSource,
            minDate,
            maxDate,
            durationSelectorPresets: swSettings.current.datePickerPresets,
            comparedDurationAllowed: state?.periodOverPeriodEnabled === true && params.mode === "single",
        };
    };
    const currentStateParamsObj = useMemo(() => {
        return getStateParams(params, state);
    }, [params, state, stateResolvedValue]);

    const updateUserPreferences = async ({ country, duration, comparedDuration, webSource }) => {
        try {
            await PreferencesService.add({
                [SegmentsUtils.getSegmentsAnalysisPrefKey(params?.id)]: {
                    duration,
                    comparedDuration,
                    country,
                    webSource,
                },
            });
        } catch (e) {}
    };

    const getWebSourceFilterComponent = (wrapWithTooltip = false, selectedWebSource, params) => {
        const activeSlide: IUnlockModalConfigTypes["DeviceFilters"] = "Devices";
        const { availableWebsource } = currentStateParamsObj;
        const selectedWebSourceObj = utils.getSelectedId(selectedWebSource);
        const presentNotificationIndicator = !!params?.isWebSourceChanged;

        const component = (
            <div key="filter-websource">
                <WebSourceFilter
                    items={availableWebsource}
                    onChange={onWebSourceChange}
                    selectedIds={selectedWebSourceObj}
                    height={60}
                    appendTo="body"
                    disabled={availableWebsource.length == 1}
                    onToggle={trackFilterDropDownStatus("Header/WebSource Filter")}
                    presentNotificationIndicator={presentNotificationIndicator}
                    presentNotificationTooltipKey={"website.analysis.present.webSource.notification.tooltip"}
                    itemWrapper={(props) => (
                        <div
                            onClick={() => {
                                setIsWebSourceHookOpen(true);
                                allTrackers.trackEvent(
                                    "hook/Contact Us/Pop Up",
                                    "click",
                                    `WebSource Filter/${props.text}`,
                                );
                            }}
                        >
                            {props.children}
                        </div>
                    )}
                />
                <UnlockModal
                    isOpen={isWebSourceHookOpen}
                    onCloseClick={() => {
                        setIsWebSourceHookOpen(false);
                    }}
                    activeSlide={activeSlide}
                    location="Hook PRO/Segment Analysis/Web Source Filter"
                    {...unlockModalConfig().DeviceFilters}
                />
            </div>
        );
        return wrapWithTooltip ? (
            <div
                className="scss-tooltip scss-tooltip--s scss-tooltip--autohiding"
                data-scss-tooltip={i18nFilter()("wa.overview.websource.tooltip")}
            >
                {component}
            </div>
        ) : (
            component
        );
    };

    const getFilters = (): IFilter[] => {
        const selectedCountryId = utils.getSelectedId(currentStateParamsObj.selectedCountry);
        const { selectedWebSource, selectedDuration, selectedCountry, availableCountries } = currentStateParamsObj;
        const params = proNavigator.getParams();
        const isCountryChanged = !!params?.isCountryChanged;
        const filters = [];
        if (selectedDuration) {
            filters.push({
                id: "duration",
                filter: getDurationSelectorComponent(),
            });
        }
        if (selectedCountry && isCountryFilterAvailable(proNavigator.current())) {
            filters.push({
                id: "country",
                filter: (
                    <div key="filter-country">
                        <CountryFilter
                            disabled={unsupportedFilter}
                            key="filter-country"
                            availableCountries={availableCountries}
                            changeCountry={onCountryChange}
                            selectedCountryIds={selectedCountryId || {}}
                            dropdownPopupPlacement="bottom-right"
                            appendTo="body"
                            height={60}
                            onToggle={trackFilterDropDownStatus("Header/Country Filter")}
                            itemWrapper={(props) => (
                                <div
                                    onClick={() => {
                                        handleCTAClick(EHooksVersions.V5, "WebCountry", TestCases.TRIAL_AFTER_TRIAL);
                                    }}
                                >
                                    {props.children}
                                </div>
                            )}
                            presentNotificationIndicator={isCountryChanged}
                            presentNotificationTooltipKey={"website.analysis.present.country.notification.tooltip"}
                        />
                    </div>
                ),
            });
        }

        if (selectedWebSource) {
            filters.push({
                id: "webSource",
                key: "webSource",
                filter: getWebSourceFilterComponent(false, selectedWebSource, params),
            });
        }
        return filters;
    };

    const isCountryFilterAvailable = (state) => {
        return state.data?.filtersConfig?.country !== FiltersEnum.HIDDEN;
    };

    const getDurationSelectorComponent = () => {
        const { isDurationChanged } = params;
        const presentNotificationForDurationChanged = isDurationChanged === true;
        const simpleDurationSelectorComponent = () => {
            return (
                <DurationSelectorSimple
                    key="filter-duration"
                    minDate={currentStateParamsObj.minDate}
                    maxDate={currentStateParamsObj.maxDate}
                    isDisabled={unsupportedFilter || currentStateParamsObj.isDurationDisabled}
                    presets={preparePresets(currentStateParamsObj.durationSelectorPresets)}
                    initialPreset={currentStateParamsObj.selectedDuration}
                    initialComparedDuration={currentStateParamsObj.selectedComparedDuration}
                    compareLabel={i18nFilter()("websiteanalysis.duration.compare.label")}
                    compareDisabledTooltipText={i18nFilter()("websiteanalysis.duration.compare.disabled.tooltip")}
                    placement="bottom-right"
                    appendTo="body"
                    onSubmit={onChangeDuration}
                    componentName={swSettings.current.componentId}
                    compareAllowed={currentStateParamsObj.comparedDurationAllowed}
                    keys={[]}
                    hasPermissionsLock={swSettings.current.durationPaymentHook}
                    presentNotificationIndicator={presentNotificationForDurationChanged}
                    presentNotificationTooltipKey={"website.analysis.present.duration.notification.tooltip"}
                />
            );
        };
        return currentStateParamsObj.comparedDurationAllowed ? (
            <PeriodOverPeriodWrapper>{simpleDurationSelectorComponent()}</PeriodOverPeriodWrapper>
        ) : swSettings.user.isShortMonthIntervalsUser ? (
            <DurationSelectorPresetOriented
                minDate={currentStateParamsObj.minDate}
                maxDate={currentStateParamsObj.maxDate}
                isDisabled={unsupportedFilter || currentStateParamsObj.isDurationDisabled}
                onSubmit={onChangeDuration}
                presets={preparePresets(currentStateParamsObj.durationSelectorPresets)}
                initialPreset={currentStateParamsObj.selectedDuration}
            />
        ) : (
            <> {simpleDurationSelectorComponent()}</>
        );
    };

    const onChangeDuration = (duration, comparedDuration) => {
        // @ts-expect-error FIXME: Argument of type 'IRouterState' is not assignable to parameter of type 'string'
        if (!swSettings.allowedDuration(duration, state)) {
            return;
        }
        if (!skipFilterConsistency) {
            durationSelected(duration);
        }

        proNavigator.updateParams({
            duration,
            comparedDuration: comparedDuration ? comparedDuration : "",
            isDurationChanged: null,
        });
        updateUserPreferences({
            duration,
            comparedDuration: comparedDuration ? comparedDuration : undefined,
            country: params.country,
            webSource: params.webSource,
        });
    };

    const onCountryChange = (country) => {
        if (!country) {
            return;
        }
        if (!skipFilterConsistency) {
            updateFilterConsistencyCountry(country.id);
        }
        proNavigator.updateParams({
            country: country.id,
            isCountryChanged: null,
        });
        updateUserPreferences({
            duration: params.duration,
            comparedDuration: params.comparedDuration,
            country: String(country.id),
            webSource: params.webSource,
        });
    };

    const onWebSourceChange = (webSource) => {
        if (!webSource) {
            return;
        }
        if (!skipFilterConsistency) {
            webSourceSelected(webSource.id);
        }
        allTrackers.dispatchTrackEvent("Drop Down", "click", `Header/WebSource Filter/${webSource.text}`);
        proNavigator.updateParams({
            webSource: webSource.id,
            isWebSourceChanged: null,
        });
        updateUserPreferences({
            duration: params.duration,
            comparedDuration: params.comparedDuration,
            country: String(params.country.id),
            webSource: params.webSource,
        });
    };

    const trackFilterDropDownStatus = (eventName: string) => {
        return (isOpen: boolean) => {
            SwTrack.all.trackEvent("Drop down", isOpen ? "open" : "close", eventName);
        };
    };

    return (
        <div className="react-filters-container">
            <FiltersBar filters={getFilters()} />
        </div>
    );
};
